// AlertPreferences.jsx
//
// Company admin's alert-routing page. Lives at /admin/alert-preferences.
//
// What it does:
//   - Toggle Slack / Teams / Email channels independently.
//   - Webhook URL for each (write-only; once saved the FE shows
//     "configured ✓" so we don't echo it back from the API for
//     security reasons).
//   - Email recipients (multi).
//   - Subscription checkboxes per alert type.
//   - Brand filter (multi-select). Empty = all brands.
//   - "Send test alert" button — fires a synthetic alert through the
//     same queue a real one would take, so the operator can verify
//     routing before the first incident hits.
//
// Polls the backend on every save so the displayed state matches
// what the worker will see.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAlertConfig, saveAlertConfig, testAlertConfig } from '../../api/alertConfig/alertConfig';
import { get } from '../../api';

const ALERT_TYPES = [
  { key: 'llm_cap',         label: 'LLM cost cap reached',     description: 'Daily or monthly USD cap hit — every LLM call is now blocked.' },
  { key: 'mailbox_paused',  label: 'Mailbox auto-paused',      description: 'Bounce or complaint rate exceeded threshold; sending paused on that mailbox.' },
  { key: 'brand_issue',     label: 'Brand-specific issue',     description: 'OAuth token refresh failed, social account disconnected, etc. Includes context.brandId.' },
  { key: 'trial_expiring',  label: 'Trial expiring soon',      description: 'Reminder at the 7-/3-/1-day thresholds before the trial ends.' },
  { key: 'queue_failures',  label: 'Queue failure spikes',     description: 'Real (non-phantom) job failures crossing 10/hour. Noisy — opt-in.' },
];

function Section({ title, subtitle, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: 18, marginBottom: 14 }}>
      <div style={{ marginBottom: 12 }}>
        <h5 style={{ margin: 0 }}>{title}</h5>
        {subtitle && <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function ChannelRow({ label, description, enabled, onToggle, children }) {
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, fontSize: 13 }}>
        <input type="checkbox" checked={!!enabled} onChange={onToggle} />
        {label}
      </label>
      <div style={{ fontSize: 11, color: '#888', marginLeft: 24, marginBottom: 6 }}>{description}</div>
      {enabled && <div style={{ marginLeft: 24 }}>{children}</div>}
    </div>
  );
}

export default function AlertPreferences() {
  const [config, setConfig] = useState(null);
  const [brands, setBrands] = useState([]);
  const [busy,   setBusy]   = useState(false);
  const [err,    setErr]    = useState(null);
  const [msg,    setMsg]    = useState(null);

  const refresh = useCallback(async () => {
    setErr(null);
    try {
      const [c, bs] = await Promise.all([
        getAlertConfig(),
        get('/api/brands/for-user').catch(() => ({ brands: [] })),
      ]);
      setConfig((c && c.data) || null);
      setBrands((bs && (bs.brands || bs.data)) || []);
    } catch (e) { setErr((e && (e.message || (e.response && e.response.data && e.response.data.error))) || 'Failed to load'); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Local edits on a draft so the page doesn't round-trip the server
  // on every toggle. The Save button persists, then we re-fetch.
  const setChannel = (kind, patch) => setConfig((c) => ({
    ...c,
    channels: { ...c.channels, [kind]: { ...c.channels[kind], ...patch } },
  }));
  const setSubscription = (key, value) => setConfig((c) => ({
    ...c, subscriptions: { ...c.subscriptions, [key]: value },
  }));
  const toggleBrand = (id) => setConfig((c) => {
    const cur = Array.isArray(c.brandFilter) ? c.brandFilter : [];
    return { ...c, brandFilter: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });

  const save = async () => {
    if (!config) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await saveAlertConfig({
        channels:      config.channels,
        subscriptions: config.subscriptions,
        brandFilter:   config.brandFilter || [],
      });
      setMsg('Saved — alerts will route accordingly within a minute.');
      setTimeout(() => setMsg(null), 4000);
      refresh();
    } catch (e) { setErr((e && (e.message || (e.response && e.response.data && e.response.data.error))) || 'Save failed'); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await testAlertConfig();
      setMsg('Test alert queued. It should land on every enabled channel within a minute.');
      setTimeout(() => setMsg(null), 8000);
    } catch (e) { setErr((e && (e.message || (e.response && e.response.data && e.response.data.error))) || 'Test failed'); }
    finally { setBusy(false); }
  };

  // Email-address editing: a comma-separated input that we round-trip
  // to/from the array on the doc. Keeps the form small without
  // pulling in a tag-style multi-input component.
  const emailString = useMemo(() => (
    (config && config.channels && config.channels.email && Array.isArray(config.channels.email.addresses))
      ? config.channels.email.addresses.join(', ')
      : ''
  ), [config]);
  const setEmailString = (s) => setChannel('email', { addresses: String(s).split(/[,\s]+/).map((x) => x.trim()).filter(Boolean) });

  if (!config) {
    return (
      <div style={{ padding: 20 }}>
        <h3 style={{ margin: 0 }}>Alert Preferences</h3>
        {err ? <div className="alert alert-warning" style={{ fontSize: 13, marginTop: 8 }}>{err}</div> : <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>Loading…</div>}
      </div>
    );
  }

  return (
    <div style={{ padding: 20, maxWidth: 920 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <h3 style={{ margin: 0 }}>Alert Preferences</h3>
          <div style={{ fontSize: 12, color: '#666' }}>Where to send operational alerts for this company — and which alerts to receive.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="acc-btn" onClick={sendTest} disabled={busy}>Send test alert</button>
          <button className="acc-btn acc-btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>

      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      {msg && <div className="alert alert-success" style={{ fontSize: 13 }}>{msg}</div>}

      <Section title="Channels" subtitle="Each channel fires independently. Disable one without losing its config.">
        <ChannelRow
          label="Slack"
          description="Incoming Webhook URL. Find it under your Slack workspace → Apps → Incoming Webhooks."
          enabled={config.channels.slack && config.channels.slack.enabled}
          onToggle={() => setChannel('slack', { enabled: !(config.channels.slack && config.channels.slack.enabled) })}
        >
          <input type="text" className="form-control" placeholder="https://hooks.slack.com/services/..."
                 value={(config.channels.slack && config.channels.slack.webhookUrl) || ''}
                 onChange={(e) => setChannel('slack', { webhookUrl: e.target.value })}
                 style={{ fontSize: 13 }} />
        </ChannelRow>

        <ChannelRow
          label="Microsoft Teams"
          description="Incoming Webhook URL from a Power Automate Workflow with an HTTP trigger that posts to a Teams channel."
          enabled={config.channels.teams && config.channels.teams.enabled}
          onToggle={() => setChannel('teams', { enabled: !(config.channels.teams && config.channels.teams.enabled) })}
        >
          <input type="text" className="form-control" placeholder="https://prod-..../IncomingWebhook/..."
                 value={(config.channels.teams && config.channels.teams.webhookUrl) || ''}
                 onChange={(e) => setChannel('teams', { webhookUrl: e.target.value })}
                 style={{ fontSize: 13 }} />
        </ChannelRow>

        <ChannelRow
          label="Email"
          description="Comma-separated list of recipient addresses (max 20)."
          enabled={config.channels.email && config.channels.email.enabled}
          onToggle={() => setChannel('email', { enabled: !(config.channels.email && config.channels.email.enabled) })}
        >
          <input type="text" className="form-control" placeholder="ops@yourco.com, cto@yourco.com"
                 value={emailString}
                 onChange={(e) => setEmailString(e.target.value)}
                 style={{ fontSize: 13 }} />
        </ChannelRow>
      </Section>

      <Section title="Subscriptions" subtitle="Which alert types to receive. Queue-failure alerts are noisy — opt-in only.">
        {ALERT_TYPES.map((t) => (
          <label key={t.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
            <input type="checkbox" checked={!!(config.subscriptions && config.subscriptions[t.key])}
                   onChange={(e) => setSubscription(t.key, e.target.checked)} style={{ marginTop: 3 }} />
            <div>
              <div style={{ fontWeight: 600 }}>{t.label}</div>
              <div style={{ fontSize: 11, color: '#888' }}>{t.description}</div>
            </div>
          </label>
        ))}
      </Section>

      <Section title="Brand filter" subtitle={`Empty = receive alerts for all brands. If you select brands, ONLY alerts touching those brands will fire. ${brands.length === 0 ? '(No brands yet.)' : ''}`}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {brands.map((b) => {
            const id = String(b._id || b.id || '');
            const on = Array.isArray(config.brandFilter) && config.brandFilter.map(String).includes(id);
            return (
              <button key={id} type="button"
                      onClick={() => toggleBrand(id)}
                      className="acc-btn"
                      style={{ fontSize: 12, padding: '4px 10px',
                               background: on ? '#15172b' : '#fff',
                               color: on ? '#fff' : '#15172b',
                               borderColor: on ? '#15172b' : '#d1d5db' }}>
                {b.name || id}
              </button>
            );
          })}
        </div>
      </Section>
    </div>
  );
}
